import dayjs from 'dayjs'
import { DayjsFormats } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { Base } from 'models'
import { type ActivityType } from 'entities/Activity/model/types'
import { type IResponseActivity } from 'entities/Activity/api/types'
import { type IConversationsHistoryInfo } from 'entities/Message'
import type { IEntitiesStore } from 'entities/EntitiesExport'

type IActivity = {
  item: IResponseActivity
  conversationsHistoryInfo?: IConversationsHistoryInfo
}

export class Activity extends Base {
  assignedUser: IResponseActivity['assignedUser']
  additionalInfo: IResponseActivity['additional_info']
  user: IResponseActivity['user']
  relatedContact: IResponseActivity['relatedContact']
  conversation_id: number
  created_at: string
  id: number
  item_type: 'activity_log'
  organization_id: number
  sorted_date: string
  type: ActivityType | string
  updated_at: string
  conversations_history_info?: IConversationsHistoryInfo

  constructor({ item, conversationsHistoryInfo }: IActivity, private _entities: IEntitiesStore) {
    super(item.id)
    this.assignedUser = item.assignedUser
    this.conversation_id = item.conversation_id
    this.created_at = item.created_at
    this.id = item.id
    this.item_type = item.item_type
    this.organization_id = item.organization_id
    this.sorted_date = item.sorted_date
    this.type = item.type
    this.updated_at = item.updated_at
    this.user = item.user
    this.relatedContact = item.relatedContact
    this.additionalInfo = item.additional_info

    if (conversationsHistoryInfo) {
      this.conversations_history_info = conversationsHistoryInfo
    }
  }

  get user_id() {
    return this.user?.user_id
  }

  get assigned_user_id() {
    return this.assignedUser?.user_id
  }

  get date() {
    return this.sorted_date || this.created_at
  }

  get sortDateTime() {
    return dayjs(this.sorted_date || this.date).valueOf()
  }

  get day() {
    return uiStore.dayjs(this.date).format(DayjsFormats.dateSort)
  }

  get text() {
    const user = this._entities.usersStore.getItem(this.user.user_id)
    const assignedUser =
      this.assignedUser && this._entities.usersStore.getItem(this.assignedUser?.user_id)
    const contact =
      this.relatedContact && this._entities.contactsStore.getItem(this.relatedContact?.user_id)

    const userName = user?.displayInfo || `${this.user.first_name} ${this.user.last_name}`
    const assignedUserName =
      assignedUser?.displayInfo ||
      `${this.assignedUser?.first_name} ${this.assignedUser?.last_name}`

    const organizationName = this.additionalInfo?.organization_name
    const inboxName = this.additionalInfo?.team_name
    const chatbotName = this.additionalInfo?.chatbot?.name
    const contactName =
      contact?.name ||
      `${this.relatedContact?.first_name ?? ''} ${this.relatedContact?.last_name ?? ''}`.trim() ||
      this.relatedContact?.number

    if (this.type === 'reassign') {
      if (assignedUser) {
        return `${userName} reassigned the conversation to ${assignedUserName}`
      }
      return `${userName} unassigned the conversation`
    }
    if (this.type === 'marked') {
      return `${userName} made priority the conversation`
    }
    if (this.type === 'unmarked') {
      return `${userName} unmade priority the conversation`
    }
    if (this.type === 'reopened') {
      return `${userName || contactName} ${this.type} the conversation`
    }
    if (this.type === 'responded') {
      return `Marked as responded by ${userName}`
    }
    if (this.type === 'response_needed') {
      return `Marked as Needs Response by ${userName}`
    }

    if (this.type === 'transfer_from') {
      return `Conversation was transferred from ${organizationName} / ${inboxName} by ${userName}`
    }

    if (this.type === 'transfer_to') {
      return `Conversation was transferred to ${organizationName} / ${inboxName} by ${userName}`
    }
    if (this.type === 'replied') {
      return `${contactName} reopened the conversation`
    }

    if (this.type === 'chatbot_paused') {
      return `${userName} paused the AI Agent: ${chatbotName}`
    }

    if (this.type === 'chatbot_resumed_manually') {
      return `${userName} resumed the AI Agent: ${chatbotName}`
    }

    if (this.type === 'chatbot_resumed_automatically') {
      return `${userName} auto-resumed the AI Agent: ${chatbotName}`
    }

    if (this.type === 'chatbot_execution_started') {
      return `Contact has been enrolled into AI Agent: ${chatbotName}`
    }
    if (this.type === 'chatbot_finish_success') {
      return `AI Agent: ${chatbotName} completed successfully`
    }
    if (this.type === 'chatbot_finish_fallback') {
      return `AI Agent: ${chatbotName} completed with fallback`
    }

    if (this.type === 'chatbot_claimed' || this.type === 'chatbot_finish_stop_disabled') {
      return `${userName} stopped the AI Agent: ${chatbotName}`
    }

    if (this.type === 'chatbot_finish_stop') {
      return `AI Agent: ${chatbotName} stopped`
    }
    if (this.type === 'chatbot_finish_stop_disqualified') {
      return `AI Agent: ${chatbotName} stopped - contact not qualified`
    }
    if (this.type === 'chatbot_enrollment_skipped') {
      return `Skipped by ${chatbotName}. This contact is already handled by another AI Agent on this number.`
    }
    if (this.type === 'chatbot_finish_handoff') {
      return `AI Agent: ${chatbotName} handed off conversation to human`
    }

    return `${userName} ${this.type} the conversation`
  }
  get isConversationsHistory() {
    return Boolean(this.conversations_history_info)
  }
  get teamName() {
    return this.conversations_history_info?.teamName || ''
  }
  get teamId() {
    return this.conversations_history_info?.teamId
  }
}
